import React, { useEffect, useState, useRef } from 'react';
import { Clock, AlertCircle, SmartphoneIcon, QrCodeIcon, CheckCircle2Icon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { formatPrice } from '../../utils/formatters';
interface RaastQRPaymentProps {
  totalAmount: number;
  onComplete: () => void;
  onCancel: () => void;
}
export function RaastQRPayment({
  totalAmount,
  onComplete,
  onCancel
}: RaastQRPaymentProps) {
  // 5 minutes in seconds
  const [timeLeft, setTimeLeft] = useState(300);
  const [isExpired, setIsExpired] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Countdown timer for QR validity (5 mins)
  useEffect(() => {
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }
    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [timeLeft]);
  // Auto-redirect timer (50 seconds) to simulate successful payment
  useEffect(() => {
    const successTimerId = setTimeout(() => {
      onCompleteRef.current();
    }, 50000);
    return () => clearTimeout(successTimerId);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="flex flex-col items-center justify-center py-6 text-center" data-id="element-554">
      <div className="mb-6" data-id="element-555">
        <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-12 object-contain mx-auto mb-2" data-id="element-556" />
        <h2 className="text-xl font-bold text-gray-900" data-id="element-557">
          Scan to Pay {formatPrice(totalAmount)}
        </h2>
        <p className="text-sm text-gray-500 mt-1" data-id="element-558">
          Open your banking app and scan the QR code below
        </p>
      </div>

      {isExpired ? <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-sm w-full flex flex-col items-center" data-id="element-559">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" data-id="element-560" />
          <h3 className="text-lg font-bold text-red-900 mb-2" data-id="element-561">
            QR Code Expired
          </h3>
          <p className="text-sm text-red-700 mb-6 text-center" data-id="element-562">
            This payment session has expired. Please generate a new QR code to
            continue.
          </p>
          <button onClick={onCancel} className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-563">
            Return to Checkout
          </button>
        </div> : <div className="flex flex-col items-center" data-id="element-564">
          <div className="bg-white p-4 rounded-xl shadow-sm border-2 border-gray-100 mb-6 relative" data-id="element-565">
            {/* Generic QR Code placeholder */}
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=RAAST_PAYMENT_DEMO" alt="Payment QR Code" className="w-48 h-48" data-id="element-566" />

            {/* Scanning animation overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-400/20 to-transparent h-full w-full animate-[scan_2s_ease-in-out_infinite]" style={{
          backgroundSize: '100% 20%'
        }} data-id="element-567" />
          </div>

          <div className="flex items-center justify-center space-x-2 text-sm font-medium text-gray-700 bg-gray-100 px-4 py-2 rounded-full mb-6" data-id="element-568">
            <Clock className="h-4 w-4 text-blue-600" data-id="element-569" />
            <span data-id="element-570">
              QR expires in:{' '}
              <span className="text-blue-600 font-bold" data-id="element-571">
                {formatTime(timeLeft)}
              </span>
            </span>
          </div>

          {/* User Guidelines */}
          <div className="w-full max-w-sm bg-gray-50 border border-gray-200 rounded-xl mb-6 overflow-hidden text-left" data-id="element-572">
            <button onClick={() => setShowGuide(!showGuide)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-573">
              <span className="flex items-center gap-2" data-id="element-574">
                <SmartphoneIcon className="w-4 h-4 text-blue-600" data-id="element-575" />
                How to pay using QR code?
              </span>
              {showGuide ? <ChevronUpIcon className="w-4 h-4 text-gray-500" data-id="element-576" /> : <ChevronDownIcon className="w-4 h-4 text-gray-500" data-id="element-577" />}
            </button>
            {showGuide && <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-578">
                <div className="space-y-4" data-id="element-579">
                  <div className="flex items-start gap-3" data-id="element-580">
                    <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-581">
                      <span className="text-xs font-bold text-blue-700" data-id="element-582">1</span>
                    </div>
                    <div data-id="element-583">
                      <p className="text-sm font-medium text-gray-800" data-id="element-584">
                        Open your banking app
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-585">
                        Launch your bank's mobile app (e.g. HBL Mobile,
                        JazzCash, Easypaisa, Meezan, etc.)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-586">
                    <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-587">
                      <span className="text-xs font-bold text-blue-700" data-id="element-588">2</span>
                    </div>
                    <div data-id="element-589">
                      <p className="text-sm font-medium text-gray-800 flex items-center gap-1" data-id="element-590">
                        Navigate to QR Pay / Scan & Pay
                        <QrCodeIcon className="w-3.5 h-3.5 text-blue-500" data-id="element-591" />
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-592">
                        Look for "QR Pay", "Scan & Pay", or "RAAST QR" option in
                        your banking app.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-593">
                    <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-594">
                      <span className="text-xs font-bold text-blue-700" data-id="element-595">3</span>
                    </div>
                    <div data-id="element-596">
                      <p className="text-sm font-medium text-gray-800 flex items-center gap-1" data-id="element-597">
                        Scan the QR code
                        <CheckCircle2Icon className="w-3.5 h-3.5 text-green-500" data-id="element-598" />
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-599">
                        Point your phone camera at the QR code above. Verify the
                        merchant (Gadgets Zone) and amount (
                        {formatPrice(totalAmount)}).
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-600">
                    <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-601">
                      <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-602" />
                    </div>
                    <div data-id="element-603">
                      <p className="text-sm font-medium text-gray-800" data-id="element-604">
                        Confirm & authenticate
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-605">
                        Tap "Pay" and authenticate with your PIN or biometrics.
                        This page will update automatically once payment is
                        confirmed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </div>

          <p className="text-xs text-gray-500 animate-pulse" data-id="element-606">
            Waiting for payment confirmation...
          </p>

          <button onClick={onCancel} className="mt-6 text-sm text-gray-500 hover:text-gray-700 underline" data-id="element-607">
            Cancel Payment
          </button>
        </div>}
    </div>;
}